import styled from '@emotion/styled';

import { useSmartGuides } from '../../context/SmartGuidesProvider';
import {
  isEnvironmentalPrinciplesAndConceptsSignedTip,
  isEquityAndAccessibilitySignedTip,
} from '../../modules/signed-tip/signed-tip';
import { PrintableBody1 } from '../../printable-styles/printable-typography';
import type { StepType } from '../../types/smart-guide';
import { isValidatedUSStandard } from '../../utils/standards';
import { Grouping } from '../Grouping';
import { NumberedStep } from '../NumberedStep';
import { PageBreak } from '../page-break';
import { PartDivider } from '../PartDivider';
import { PreprInnerHtml } from '../PreprInnerHtml';
import { SignedTip } from '../tips/signed-tip';

type Props = {
  step: StepType;
  index: number;
  partNumber: number;
  isPrintable?: boolean;
};

const TextWrapper = styled(PrintableBody1)`
  @media print {
    color: #000000;
  }
`;

export const RenderStep = ({ step, index, partNumber, isPrintable }: Props) => {
  const { standard } = useSmartGuides();

  const render = () => {
    switch (step.__typename) {
      case 'Text':
        if (!step.text) return null;
        return typeof step.text === 'string' ? (
          <TextWrapper>
            <PreprInnerHtml key={index} text={step.text} />
          </TextWrapper>
        ) : (
          <TextWrapper key={index}>{step.text}</TextWrapper>
        );
      case 'Agrupacin':
        return <Grouping key={index} grouping={step.text} part={partNumber} />;
      case 'PasoNumerado':
        return (
          <NumberedStep
            evaluable={step.evaluable}
            key={index}
            num={step.stepNumber}
            part={partNumber}
            isPrintable={isPrintable}
            content={step.content}
          />
        );

      case 'Subactividad':
        return <PartDivider part={partNumber} text={step.text} />;
      case 'Signedtip':
        if (
          isPrintable &&
          isValidatedUSStandard(standard ?? '') &&
          (isEquityAndAccessibilitySignedTip(step.tip_type) ||
            isEnvironmentalPrinciplesAndConceptsSignedTip(step.tip_type))
        )
          return null;

        return (
          <SignedTip
            key={`${step.__typename}-${index}`}
            type={step.tip_type}
            counselor={step.counselor}
            icon={step.icon}
            isPrintable={isPrintable}
            media={step.media}
            tables={step.tables}
            dataTestId={`${step.__typename}-${index}`}
          >
            {step.text}
          </SignedTip>
        );
      case 'PageBreak':
        return <PageBreak />;
      default:
        return null;
    }
  };

  return <div id={`${step.__typename}-${index}`}>{render()}</div>;
};
